import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { scaleLinear } from "d3-scale";
import { axisBottom, axisLeft, axisRight } from "d3-axis";
import { select } from "d3-selection";
import { line, area } from "d3-shape";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type LineGraphData = {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string; // Primary (left) Y-axis label - kept for backward compatibility
    yAxisLabels: string[]; // Array of 1-2 distinct Y-axis labels (left, then right if dual)
    isAreaChart: boolean;
    series: Array<{
        name: string;
        color: string;
        yAxisLabel: string; // Which Y-axis this series belongs to
        points: Array<{
            x: number;
            y: number;
        }>;
    }>;
};

export const isLineGraphData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());

    // Check for regular line graph (7 columns)
    if (columns.length === 7) {
        const requiredColumns = ["line_x", "line_y", "line_series", "line_color", "line_title", "line_x_axis_label", "line_y_axis_label"];
        return requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    }

    // Check for area chart (8 columns)
    if (columns.length === 8) {
        const requiredColumns = ["line_x", "line_y", "line_series", "line_color", "line_type", "line_title", "line_x_axis_label", "line_y_axis_label"];
        if (!requiredColumns.every(reqCol => columnTitles.includes(reqCol))) {
            return false;
        }
        // Verify line_type is "area"
        const typeCol = columns.find(col => (col.title || "").trim() === "line_type");
        if (typeCol && dataSource.length > 0) {
            const typeValue = String(dataSource[0]?.[typeCol.dataIndex] || "").trim().toLowerCase();
            return typeValue === "area";
        }
        return false;
    }

    return false;
};

export const extractLineGraphData = (columns: any[], dataSource: any[]): LineGraphData | null => {
    if (!isLineGraphData(columns, dataSource)) {
        return null;
    }

    const xCol = columns.find(col => (col.title || "").trim() === "line_x");
    const yCol = columns.find(col => (col.title || "").trim() === "line_y");
    const seriesCol = columns.find(col => (col.title || "").trim() === "line_series");
    const colorCol = columns.find(col => (col.title || "").trim() === "line_color");
    const titleCol = columns.find(col => (col.title || "").trim() === "line_title");
    const xAxisLabelCol = columns.find(col => (col.title || "").trim() === "line_x_axis_label");
    const yAxisLabelCol = columns.find(col => (col.title || "").trim() === "line_y_axis_label");
    const typeCol = columns.find(col => (col.title || "").trim() === "line_type");

    if (!xCol || !yCol || !seriesCol || !colorCol || !titleCol || !xAxisLabelCol || !yAxisLabelCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();
    const xAxisLabel = String(dataSource[0]?.[xAxisLabelCol.dataIndex] || "").trim();

    // Check if this is an area chart
    const isAreaChart = typeCol ? String(dataSource[0]?.[typeCol.dataIndex] || "").trim().toLowerCase() === "area" : false;

    // Collect all distinct Y-axis labels and track which label each series uses
    const yAxisLabelSet = new Set<string>();
    const seriesYAxisLabelMap = new Map<string, string>(); // series name -> y-axis label

    dataSource.forEach(row => {
        const seriesName = String(row[seriesCol.dataIndex] || "").trim();
        const yLabel = String(row[yAxisLabelCol.dataIndex] || "").trim();
        yAxisLabelSet.add(yLabel);
        // Use first row's y-axis label for each series
        if (!seriesYAxisLabelMap.has(seriesName)) {
            seriesYAxisLabelMap.set(seriesName, yLabel);
        }
    });

    // Convert to array (preserve order of first appearance)
    const yAxisLabels: string[] = [];
    dataSource.forEach(row => {
        const yLabel = String(row[yAxisLabelCol.dataIndex] || "").trim();
        if (!yAxisLabels.includes(yLabel)) {
            yAxisLabels.push(yLabel);
        }
    });

    // Limit to 2 distinct Y-axis labels
    const finalYAxisLabels = yAxisLabels.slice(0, 2);

    // Group points by series
    const seriesMap = new Map<string, { name: string; color: string; yAxisLabel: string; points: Array<{ x: number; y: number }> }>();

    dataSource.forEach(row => {
        const xStr = String(row[xCol.dataIndex] || "0").replace(/,/g, "");
        const yStr = String(row[yCol.dataIndex] || "0").replace(/,/g, "");
        const seriesName = String(row[seriesCol.dataIndex] || "").trim();
        const color = String(row[colorCol.dataIndex] || "#000000").trim();
        const yLabel = seriesYAxisLabelMap.get(seriesName) || finalYAxisLabels[0] || "";

        if (!seriesMap.has(seriesName)) {
            seriesMap.set(seriesName, {
                name: seriesName,
                color: color, // Use first point's color for the series
                yAxisLabel: yLabel,
                points: []
            });
        }

        seriesMap.get(seriesName)!.points.push({
            x: parseFloat(xStr) || 0,
            y: parseFloat(yStr) || 0
        });
    });

    // Sort points within each series by x-value and convert to array
    const series = Array.from(seriesMap.values()).map(seriesData => ({
        ...seriesData,
        points: seriesData.points.sort((a, b) => a.x - b.x)
    }));

    // Use first Y-axis label for backward compatibility
    const yAxisLabel = finalYAxisLabels[0] || "";

    return {
        title,
        xAxisLabel: xAxisLabel || undefined,
        yAxisLabel: yAxisLabel || undefined,
        yAxisLabels: finalYAxisLabels,
        isAreaChart,
        series
    };
};

export function LineGraph(props: {
    data: LineGraphData;
    darkMode: boolean;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 1200);
                const chartHeight = Math.min(chartWidth * 0.7, 800);

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current || data.series.length === 0) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        // Check if we have dual Y-axis mode
        const isDualYAxis = data.yAxisLabels.length === 2;
        const leftYAxisLabel = data.yAxisLabels[0] || "";
        const rightYAxisLabel = data.yAxisLabels[1] || "";

        // Increase right margin if dual Y-axis to fit second axis + legend
        const margin = { top: 40, right: isDualYAxis ? 350 : 200, bottom: 60, left: 80 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Calculate domain for X-axis (all series)
        const allXValues: number[] = [];
        data.series.forEach(series => {
            series.points.forEach(point => {
                allXValues.push(point.x);
            });
        });

        const xMin = Math.min(...allXValues);
        const xMax = Math.max(...allXValues);

        const xScale = scaleLinear()
            .domain([xMin - (xMax - xMin) * 0.05, xMax + (xMax - xMin) * 0.05])
            .range([0, width]);

        // Calculate Y domains separately for left and right axes
        const leftYValues: number[] = [];
        const rightYValues: number[] = [];

        data.series.forEach(series => {
            const isRightAxis = isDualYAxis && series.yAxisLabel === rightYAxisLabel;
            series.points.forEach(point => {
                if (isRightAxis) {
                    rightYValues.push(point.y);
                } else {
                    leftYValues.push(point.y);
                }
            });
        });

        // Ensure we have values for single Y-axis mode
        if (leftYValues.length === 0 && rightYValues.length > 0) {
            leftYValues.push(...rightYValues);
        }

        const leftYMin = leftYValues.length > 0 ? Math.min(...leftYValues) : 0;
        const leftYMax = leftYValues.length > 0 ? Math.max(...leftYValues) : 1;
        const rightYMin = rightYValues.length > 0 ? Math.min(...rightYValues) : 0;
        const rightYMax = rightYValues.length > 0 ? Math.max(...rightYValues) : 1;

        const yScaleLeft = scaleLinear()
            .domain([leftYMin - (leftYMax - leftYMin) * 0.05, leftYMax + (leftYMax - leftYMin) * 0.05])
            .range([height, 0]);

        const yScaleRight = scaleLinear()
            .domain([rightYMin - (rightYMax - rightYMin) * 0.05, rightYMax + (rightYMax - rightYMin) * 0.05])
            .range([height, 0]);

        // Helper to get correct Y scale for a series
        const getYScale = (seriesYAxisLabel: string) => {
            if (isDualYAxis && seriesYAxisLabel === rightYAxisLabel) {
                return yScaleRight;
            }
            return yScaleLeft;
        };

        // Set background to transparent
        svg.append("rect")
            .attr("width", dimensions.width)
            .attr("height", dimensions.height)
            .attr("fill", "transparent");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Grid lines (use left Y-axis for horizontal grid)
        g.append("g")
            .attr("class", "grid")
            .selectAll("line")
            .data(xScale.ticks(10))
            .enter()
            .append("line")
            .attr("x1", d => xScale(d))
            .attr("x2", d => xScale(d))
            .attr("y1", 0)
            .attr("y2", height)
            .attr("stroke", darkMode ? "#404040" : "#e0e0e0")
            .attr("stroke-width", 1)
            .attr("opacity", 0.5);

        g.append("g")
            .attr("class", "grid")
            .selectAll("line")
            .data(yScaleLeft.ticks(10))
            .enter()
            .append("line")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("y1", d => yScaleLeft(d))
            .attr("y2", d => yScaleLeft(d))
            .attr("stroke", darkMode ? "#404040" : "#e0e0e0")
            .attr("stroke-width", 1)
            .attr("opacity", 0.5);

        // X-axis
        g.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(axisBottom(xScale))
            .selectAll("text")
            .attr("fill", darkMode ? "#ffffff" : "#000000");

        // Left Y-axis
        const leftAxisGroup = g.append("g")
            .call(axisLeft(yScaleLeft));
        leftAxisGroup.selectAll("text")
            .attr("fill", darkMode ? "#ffffff" : "#000000");

        // Right Y-axis (only if dual Y-axis mode)
        if (isDualYAxis) {
            const rightAxisGroup = g.append("g")
                .attr("transform", `translate(${width}, 0)`)
                .call(axisRight(yScaleRight));
            rightAxisGroup.selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");
            rightAxisGroup.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");
        }

        // Axis lines
        g.selectAll(".domain, .tick line")
            .attr("stroke", darkMode ? "#666666" : "#cccccc");

        // X-axis label
        if (data.xAxisLabel) {
            g.append("text")
                .attr("x", width / 2)
                .attr("y", height + margin.bottom - 10)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(data.xAxisLabel);
        }

        // Left Y-axis label
        if (leftYAxisLabel) {
            g.append("text")
                .attr("transform", "rotate(-90)")
                .attr("y", -margin.left + 20)
                .attr("x", -height / 2)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(leftYAxisLabel);
        }

        // Right Y-axis label (only if dual Y-axis mode)
        if (isDualYAxis && rightYAxisLabel) {
            g.append("text")
                .attr("transform", "rotate(90)")
                .attr("y", -width - 60)
                .attr("x", height / 2)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(rightYAxisLabel);
        }

        // Calculate baseline for area charts
        const baselineLeft = Math.max(0, leftYMin - (leftYMax - leftYMin) * 0.05);
        const baselineRight = Math.max(0, rightYMin - (rightYMax - rightYMin) * 0.05);

        // Draw areas or lines for each series
        data.series.forEach(seriesData => {
            const yScale = getYScale(seriesData.yAxisLabel);
            const isRightAxis = isDualYAxis && seriesData.yAxisLabel === rightYAxisLabel;
            const baseline = isRightAxis ? baselineRight : baselineLeft;

            // Line generator for this series
            const lineGenerator = line<{ x: number; y: number }>()
                .x(d => xScale(d.x))
                .y(d => yScale(d.y));

            // Area generator for this series
            const areaGenerator = area<{ x: number; y: number }>()
                .x(d => xScale(d.x))
                .y0(yScale(baseline))
                .y1(d => yScale(d.y));

            if (data.isAreaChart) {
                // Render area chart with filled areas
                const areaPath = g.append("path")
                    .datum(seriesData.points)
                    .attr("fill", seriesData.color)
                    .attr("fill-opacity", 0.6)
                    .attr("stroke", "none")
                    .attr("d", areaGenerator)
                    .on("mouseover", function(this: SVGPathElement) {
                        select(this)
                            .attr("fill-opacity", 0.8);
                    })
                    .on("mouseout", function(this: SVGPathElement) {
                        select(this)
                            .attr("fill-opacity", 0.6);
                    });

                // Render line on top of area for better definition
                g.append("path")
                    .datum(seriesData.points)
                    .attr("fill", "none")
                    .attr("stroke", seriesData.color)
                    .attr("stroke-width", 2)
                    .attr("d", lineGenerator)
                    .on("mouseover", function(this: SVGPathElement) {
                        select(this)
                            .attr("opacity", 0.7)
                            .attr("stroke-width", 3);
                        areaPath.attr("fill-opacity", 0.8);
                    })
                    .on("mouseout", function(this: SVGPathElement) {
                        select(this)
                            .attr("opacity", 1)
                            .attr("stroke-width", 2);
                        areaPath.attr("fill-opacity", 0.6);
                    });
            } else {
                // Render regular line graph
                g.append("path")
                    .datum(seriesData.points)
                    .attr("fill", "none")
                    .attr("stroke", seriesData.color)
                    .attr("stroke-width", 2)
                    .attr("d", lineGenerator)
                    .on("mouseover", function(this: SVGPathElement) {
                        select(this)
                            .attr("opacity", 0.7)
                            .attr("stroke-width", 3);
                    })
                    .on("mouseout", function(this: SVGPathElement) {
                        select(this)
                            .attr("opacity", 1)
                            .attr("stroke-width", 2);
                    });
            }

            // Add points as circles
            const pointsGroup = g.append("g")
                .attr("class", `series-${seriesData.name.replace(/\s+/g, '-')}`);

            pointsGroup.selectAll(".point")
                .data(seriesData.points)
                .enter()
                .append("circle")
                .attr("class", "point")
                .attr("cx", d => xScale(d.x))
                .attr("cy", d => yScale(d.y))
                .attr("r", 4)
                .attr("fill", seriesData.color)
                .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                .attr("stroke-width", 2)
                .on("mouseover", function(this: SVGCircleElement, event: MouseEvent, d: { x: number; y: number }) {
                    select(this)
                        .attr("opacity", 0.7)
                        .attr("r", 6);

                    // Show tooltip
                    const tooltipText = `${seriesData.name}: (${d.x.toLocaleString()}, ${d.y.toLocaleString()})`;
                    const tooltip = g.append("g")
                        .attr("class", "tooltip")
                        .attr("transform", `translate(${xScale(d.x)},${yScale(d.y) - 15})`);

                    const textElement = tooltip.append("text")
                        .attr("text-anchor", "middle")
                        .attr("fill", darkMode ? "#ffffff" : "#000000")
                        .attr("font-size", "11px")
                        .text(tooltipText);

                    const textBBox = textElement.node()?.getBBox();
                    if (textBBox) {
                        tooltip.insert("rect", "text")
                            .attr("x", textBBox.x - 5)
                            .attr("y", textBBox.y - 2)
                            .attr("width", textBBox.width + 10)
                            .attr("height", textBBox.height + 4)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);
                    }
                })
                .on("mouseout", function(this: SVGCircleElement) {
                    select(this)
                        .attr("opacity", 1)
                        .attr("r", 4);

                    g.selectAll(".tooltip").remove();
                });
        });

        // Legend - position further right when dual Y-axis to avoid overlapping the right axis
        const legendXOffset = isDualYAxis ? 90 : 20;
        const legend = svg.append("g")
            .attr("class", "legend")
            .attr("transform", `translate(${dimensions.width - margin.right + legendXOffset}, ${margin.top + 20})`);

        if (isDualYAxis) {
            // Group series by axis
            const leftAxisSeries = data.series.filter(s => s.yAxisLabel !== rightYAxisLabel);
            const rightAxisSeries = data.series.filter(s => s.yAxisLabel === rightYAxisLabel);
            let yOffset = 0;

            // Left axis label
            if (leftAxisSeries.length > 0) {
                legend.append("text")
                    .attr("x", 0)
                    .attr("y", yOffset)
                    .attr("fill", darkMode ? "#888888" : "#999999")
                    .attr("font-size", "10px")
                    .attr("font-weight", "500")
                    .attr("text-transform", "uppercase")
                    .text("← Left Axis");
                yOffset += 18;

                // Left axis items
                leftAxisSeries.forEach((seriesData) => {
                    const item = legend.append("g")
                        .attr("transform", `translate(0, ${yOffset})`);

                    if (data.isAreaChart) {
                        item.append("rect")
                            .attr("x", 0)
                            .attr("y", -6)
                            .attr("width", 20)
                            .attr("height", 12)
                            .attr("fill", seriesData.color)
                            .attr("fill-opacity", 0.6);
                    } else {
                        item.append("line")
                            .attr("x1", 0)
                            .attr("x2", 20)
                            .attr("y1", 0)
                            .attr("y2", 0)
                            .attr("stroke", seriesData.color)
                            .attr("stroke-width", 2);
                    }

                    item.append("text")
                        .attr("x", 25)
                        .attr("y", 0)
                        .attr("fill", darkMode ? "#ffffff" : "#000000")
                        .attr("font-size", "12px")
                        .attr("dominant-baseline", "middle")
                        .text(seriesData.name);

                    yOffset += 22;
                });
            }

            // Right axis label
            if (rightAxisSeries.length > 0) {
                yOffset += 16; // Extra spacing between groups
                legend.append("text")
                    .attr("x", 0)
                    .attr("y", yOffset)
                    .attr("fill", darkMode ? "#888888" : "#999999")
                    .attr("font-size", "10px")
                    .attr("font-weight", "500")
                    .attr("text-transform", "uppercase")
                    .text("Right Axis →");
                yOffset += 18;

                // Right axis items
                rightAxisSeries.forEach((seriesData) => {
                    const item = legend.append("g")
                        .attr("transform", `translate(0, ${yOffset})`);

                    if (data.isAreaChart) {
                        item.append("rect")
                            .attr("x", 0)
                            .attr("y", -6)
                            .attr("width", 20)
                            .attr("height", 12)
                            .attr("fill", seriesData.color)
                            .attr("fill-opacity", 0.6);
                    } else {
                        item.append("line")
                            .attr("x1", 0)
                            .attr("x2", 20)
                            .attr("y1", 0)
                            .attr("y2", 0)
                            .attr("stroke", seriesData.color)
                            .attr("stroke-width", 2);
                    }

                    item.append("text")
                        .attr("x", 25)
                        .attr("y", 0)
                        .attr("fill", darkMode ? "#ffffff" : "#000000")
                        .attr("font-size", "12px")
                        .attr("dominant-baseline", "middle")
                        .text(seriesData.name);

                    yOffset += 22;
                });
            }
        } else {
            // Single Y-axis: simple legend
            const legendItems = legend.selectAll(".legend-item")
                .data(data.series)
                .enter()
                .append("g")
                .attr("class", "legend-item")
                .attr("transform", (_d, i: number) => `translate(0, ${i * 25})`);

            if (data.isAreaChart) {
                legendItems.append("rect")
                    .attr("x", 0)
                    .attr("y", -6)
                    .attr("width", 20)
                    .attr("height", 12)
                    .attr("fill", d => d.color)
                    .attr("fill-opacity", 0.6);
            } else {
                legendItems.append("line")
                    .attr("x1", 0)
                    .attr("x2", 20)
                    .attr("y1", 0)
                    .attr("y2", 0)
                    .attr("stroke", d => d.color)
                    .attr("stroke-width", 2);
            }

            legendItems.append("text")
                .attr("x", 25)
                .attr("y", 0)
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("dominant-baseline", "middle")
                .text(d => d.name);
        }

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Set SVG dimensions
            svg.setAttribute('width', dimensions.width.toString());
            svg.setAttribute('height', dimensions.height.toString());
            svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background (transparent)
                    ctx.fillStyle = 'transparent';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    style={{ maxWidth: "100%", overflow: "visible" }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download line graph as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download line graph data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
};

